import Color from 'color'
import React from 'react'

import { Button, createStyles, Theme } from '@mui/material'
import { ButtonClassKey } from '@mui/material/Button'
import { WithStyles, withStyles } from '@mui/styles'

const TernarySelector: React.FunctionComponent<TernarySelectorPropsWithStyles> = ({
  classes,
  selectedOption,
  onSelect = () => undefined,
}) => {
  const isYesSelected = selectedOption === SelectableOption.Yes
  const isNoSelected = selectedOption === SelectableOption.No
  const isUnknownSelected = selectedOption === SelectableOption.Unknown

  return (
    <div className={classes.container}>
      <SelectorButton
        label="Y"
        classes={{
          root: isYesSelected ? classes.yesButtonSelected : classes.yesButtonUnselected,
          text: isYesSelected ? classes.buttonLabelSelected : classes.buttonLabelUnselected,
        }}
        onClick={() => {
          onSelect(SelectableOption.Yes)
        }}
      />
      <SelectorButton
        label="N"
        classes={{
          root: isNoSelected ? classes.noButtonSelected : classes.noButtonUnselected,
          text: isNoSelected ? classes.buttonLabelSelected : classes.buttonLabelUnselected,
        }}
        onClick={() => {
          onSelect(SelectableOption.No)
        }}
      />
      <SelectorButton
        label="?"
        classes={{
          root: isUnknownSelected ? classes.naButtonSelected : classes.naButtonUnselected,
          text: isUnknownSelected ? classes.buttonLabelSelected : classes.buttonLabelUnselected,
        }}
        onClick={() => {
          onSelect(SelectableOption.Unknown)
        }}
      />
    </div>
  )
}

type TernarySelectorProps = {
  selectedOption?: SelectableOption
  onSelect?: (selectedOption: SelectableOption) => void
}

export enum SelectableOption {
  Yes = 'Yes',
  No = 'No',
  Unknown = 'Unknown',
}

type TernarySelectorPropsWithStyles = TernarySelectorProps & WithStyles<typeof styles>

function SelectorButton({ label, classes = {}, onClick = () => undefined }: SelectorButtonProps) {
  return (
    <Button classes={classes} onClick={onClick} variant="text" size="small" color="inherit">
      {label}
    </Button>
  )
}

type SelectorButtonProps = {
  label: string
  classes?: Partial<Record<ButtonClassKey, string>>
  onClick?: () => void
}

const styles = ({ spacing, palette }: Theme) => {
  const themeHoverAlpha = Color(palette.action.hover).alpha()

  function getWhiteHover(color: string, hoverAlpha: number = themeHoverAlpha) {
    const overlayColor = Color(palette.common.white).alpha(hoverAlpha)
    const hoverBackgroundColor = Color(color).mix(overlayColor, 0.25).string()
    const hoverProperties = {
      '&:hover': {
        backgroundColor: hoverBackgroundColor,
      },
    }
    return { ...hoverProperties }
  }

  const buttonWidth = spacing(3)
  const baseButtonStyles = {
    width: buttonWidth,
    minWidth: buttonWidth,
    border: `${spacing(1 / 8)} solid ${palette.grey['400']}`,
    borderRadius: 0,
    '&:first-child': {
      borderTopLeftRadius: spacing(1 / 4),
      borderBottomLeftRadius: spacing(1 / 4),
    },
    '&:last-child': {
      borderTopRightRadius: spacing(0.25),
      borderBottomRightRadius: spacing(0.25),
    },
    '&:not(:last-child)': {
      borderRightWidth: 0,
    },
  }

  const selectedButtonStyles = {
    ...baseButtonStyles,
    fontWeight: 'bold' as React.CSSProperties['fontWeight'],
    color: palette.common.white,
  }

  const unselectedButtonStyles = {
    ...baseButtonStyles,
    color: palette.text.secondary,
  }

  return createStyles({
    container: {
      display: 'inline-flex',
      margin: 'auto',
    },
    buttonLabelUnselected: {
      color: palette.text.secondary,
    },
    buttonLabelSelected: {
      color: palette.common.white,
    },
    yesButtonUnselected: {
      ...unselectedButtonStyles,
      color: palette.extra.positive,
    },
    noButtonUnselected: {
      ...unselectedButtonStyles,
      color: palette.extra.negative,
    },
    naButtonUnselected: {
      ...unselectedButtonStyles,
      color: palette.extra.neutral,
    },
    yesButtonSelected: {
      ...selectedButtonStyles,
      ...getWhiteHover(palette.extra.positive),
      backgroundColor: palette.extra.positive,
    },
    noButtonSelected: {
      ...selectedButtonStyles,
      ...getWhiteHover(palette.extra.negative),
      backgroundColor: palette.extra.negative,
    },
    naButtonSelected: {
      ...selectedButtonStyles,
      ...getWhiteHover(palette.extra.neutral),
      backgroundColor: palette.extra.neutral,
    },
  })
}

export default withStyles(styles)(TernarySelector)
